import { ContactPhone } from "@mui/icons-material";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { BrandEnum } from "../../_common/enum/brand.enum";
import { LoaderDoubleLines } from "../../_components/loader/LoaderDoubleLines";
import { getBrandCallbackLink, getBrandPrimaryColor } from "../../_helpers/brand.helper";
import { QuoteFilters } from "../quotes/quotesFilters";
import { runCustomerAppMortgageApply } from "../../_helpers/quote-apply-flow.helper";
import { useMortgageQuotes } from "../../_components/hooks/mortgages/useMortgageQuotes";
import { useIframeAutoHeight } from "../../_components/hooks/useIframeAutoHeight";
import { useScreenCategory } from "../../_components/hooks/useScreenCategory";
import { MortgagesHeader } from "./_components/mortgagesHeader";
import { QuoteList } from "./_components/mortgagesQuoteList";
import { MortgagesStickySidebar } from "./_components/mortgagesStickySidebar";

export function Mortgages(params: { brand: BrandEnum }) {
    const { formId } = useParams();
    const { isMobile } = useScreenCategory();
    const { data, isLoading, fetchQuotes } = useMortgageQuotes(formId, params.brand);

    const [showAllQuotes, setShowAllQuotes] = useState(false);
    const listRef = useRef<HTMLDivElement>(null);

    useIframeAutoHeight([isLoading, showAllQuotes, data.length]);

    useEffect(() => {
        fetchQuotes();
    }, [fetchQuotes]);

    useEffect(() => {
        if (!isLoading && listRef.current) {
            setTimeout(() => listRef.current?.scrollIntoView(), 300);
        }
    }, [isLoading]);

    function applyForQuote(id: number, quoteId: number) {
        if (!id || !quoteId) return;
        runCustomerAppMortgageApply({
            mortgageProductRateId: id,
            quoteId,
            brand: params.brand,
        });
    }

    return (
        <Container sx={{ padding: "4px" }} maxWidth="lg">
            {isLoading ? (
                <LoaderDoubleLines />
            ) : (
                <>
                    <MortgagesHeader formId={formId} brand={params.brand} quotesCount={data.length} />

                    <Grid ref={listRef} container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <MortgagesStickySidebar enabled={!isMobile}>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={2}
                                    padding={2}
                                    marginBottom={2}
                                    borderRadius="4px"
                                    bgcolor={getBrandPrimaryColor(params.brand)}
                                    color="#fff"
                                >
                                    <Typography variant="h6">Need help?</Typography>
                                    <Typography variant="subtitle2">
                                        Our team of experts can help you find the right mortgage for you
                                    </Typography>
                                    <Button 
                                        startIcon={<ContactPhone />}
                                        sx={{ width: { xs: "100% !important", md: "auto" }, bgcolor: "#fff", "&:hover": { backgroundColor: "#f5f5f5" } }}
                                        onClick={() =>
                                            window.open(getBrandCallbackLink(params.brand), "_blank", "noopener,noreferrer")
                                        }
                                    >
                                        <Typography>Book a call with us</Typography>
                                    </Button>
                                </Box>

                                <QuoteFilters setIsSaved={() => fetchQuotes()} firstName={""} />
                            </MortgagesStickySidebar>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <QuoteList
                                data={data}
                                isMobile={isMobile}
                                showAll={showAllQuotes}
                                onShowAll={() => setShowAllQuotes(true)}
                                onApply={applyForQuote}
                                brand={params.brand}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
        </Container>
    );
}
